import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageInput from "./ImageInput";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

const defaultFor = (fields) => {
  const o = {};
  fields.forEach((f) => {
    o[f.name] = f.type === "switch" ? true : f.type === "tags" || f.type === "kv" ? [] : f.type === "number" ? 0 : "";
  });
  return o;
};

function Field({ f, value, onChange }) {
  if (f.type === "textarea")
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={f.rows || 3} placeholder={f.placeholder} className="border-slate-200 focus-visible:ring-brand" data-testid={`field-${f.name}`} />;
  if (f.type === "number")
    return <Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className="border-slate-200 focus-visible:ring-brand" data-testid={`field-${f.name}`} />;
  if (f.type === "switch")
    return <div className="flex items-center gap-3"><Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${f.name}`} /><span className="text-sm text-muted-foreground">{value ? "Active" : "Hidden"}</span></div>;
  if (f.type === "image") return <ImageInput value={value} onChange={onChange} label="" />;
  if (f.type === "select")
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="border-slate-200 focus-visible:ring-brand" data-testid={`field-${f.name}`}><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (f.type === "tags")
    return <Input value={Array.isArray(value) ? value.join(", ") : ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Comma separated" className="border-slate-200 focus-visible:ring-brand" data-testid={`field-${f.name}`} />;
  if (f.type === "kv") {
    const rows = Array.isArray(value) ? value : [];
    const upd = (i, k, v) => { const n = rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)); onChange(n); };
    return (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2">
            <Input value={r.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label" className="border-slate-200" />
            <Input value={r.value || ""} onChange={(e) => upd(i, "value", e.target.value)} placeholder="Value" className="border-slate-200" />
            <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="shrink-0 rounded-lg border border-slate-200 px-3 text-red-500"><Trash2 size={15} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...rows, { label: "", value: "" }])} className="text-sm font-600 text-brand">+ Add row</button>
      </div>
    );
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className="border-slate-200 focus-visible:ring-brand" data-testid={`field-${f.name}`} />;
}

export default function CrudManager({ title, description, endpoint, fields, columns }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(defaultFor(fields)); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...defaultFor(fields), ...item }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, form);
      else await api.post(`/admin/${endpoint}`, form);
      toast.success(editing ? "Updated successfully" : "Created successfully");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${endpoint}/${item.id}`);
      toast.success("Deleted");
      setItems((p) => p.filter((x) => x.id !== item.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const cols = columns || fields.slice(0, 3).map((f) => ({ name: f.name, label: f.label, type: f.type }));
  const filtered = q ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div data-testid={`crud-${endpoint}`}>
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-display text-2xl font-800 text-brand-navy">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-700 text-white transition-colors hover:bg-brand-navy" data-testid="crud-add-btn">
          <Plus size={17} /> Add New
        </button>
      </div>

      <div className="mb-4 relative max-w-xs">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="h-10 border-slate-200 bg-white pl-9" data-testid="crud-search" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">No records yet. Use the Add New button to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {cols.map((c) => <th key={c.name} className="px-5 py-3 font-600">{c.label}</th>)}
                  <th className="px-5 py-3 text-right font-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`row-${endpoint}`}>
                    {cols.map((c) => (
                      <td key={c.name} className="px-5 py-3 align-middle">
                        {c.type === "image" ? (
                          it[c.name] ? <img src={mediaUrl(it[c.name])} alt="" className="h-11 w-11 rounded-lg border border-slate-200 object-cover" /> : <span className="text-slate-300">—</span>
                        ) : c.type === "switch" ? (
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-600 ${it[c.name] ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{it[c.name] ? "Active" : "Hidden"}</span>
                        ) : (
                          <span className="line-clamp-1 max-w-xs text-slate-700">{String(it[c.name] ?? "—")}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => openEdit(it)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-brand hover:bg-brand-sky" data-testid="crud-edit-btn"><Pencil size={15} /></button>
                        <button onClick={() => remove(it)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-red-500 hover:bg-red-50" data-testid="crud-delete-btn"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-brand-navy">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {fields.map((f) => (
              <div key={f.name} className="space-y-2">
                {f.type !== "image" && <Label className="text-slate-700">{f.label}</Label>}
                {f.type === "image" && <Label className="text-slate-700">{f.label}</Label>}
                <Field f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-600 text-slate-600">Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-700 text-white hover:bg-brand-navy disabled:opacity-70" data-testid="crud-save-btn">
              {saving && <Loader2 size={15} className="animate-spin" />} {editing ? "Save Changes" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
